import React, { useState, useEffect } from 'react';

/**
 * IzmexMediaCircuit - Content Distribution Network
 * 
 * Media processing and multi-channel delivery.
 * Shows: SOURCE → ENCODE → CDN → CHANNELS
 */

export function IzmexMediaCircuit({ className = '' }) {
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const timer = setTimeout(() => setIsVisible(true), 300);
    return () => clearTimeout(timer);
  }, []);

  return (
    <div 
      className={`relative ${className} transition-opacity duration-1000 ${isVisible ? 'opacity-100' : 'opacity-0'}`}
      style={{ width: '260px', height: '180px' }}
    >
      <svg 
        viewBox="0 0 130 90" 
        className="w-full h-full"
        preserveAspectRatio="xMidYMid meet"
      >
        <defs>
          <filter id="mediaGlow" x="-50%" y="-50%" width="200%" height="200%">
            <feGaussianBlur stdDeviation="1" result="coloredBlur"/>
            <feMerge>
              <feMergeNode in="coloredBlur"/>
              <feMergeNode in="SourceGraphic"/>
            </feMerge>
          </filter>
          <pattern id="mediaGrid" width="10" height="10" patternUnits="userSpaceOnUse">
            <path d="M 10 0 L 0 0 0 10" fill="none" stroke="rgba(15, 118, 110, 0.08)" strokeWidth="0.3"/>
          </pattern>
        </defs>

        {/* Background grid */}
        <rect width="130" height="90" fill="url(#mediaGrid)" />

        {/* Connection paths - Pipeline with distribution */}
        <g className="connections">
          {/* Main pipeline */}
          <path d="M 18 45 L 30 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 45 45 L 55 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 70 45 L 82 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          
          {/* CDN distribution fan-out */}
          <path d="M 97 45 L 105 45 L 105 22 L 115 22" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          <path d="M 97 45 L 115 45" fill="none" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <path d="M 97 45 L 105 45 L 105 68 L 115 68" fill="none" stroke="rgba(15, 118, 110, 0.35)" strokeWidth="0.5" />
          
          {/* Cache line (dotted) */}
          <path d="M 89 38 L 89 20 L 60 20 L 60 38" fill="none" stroke="rgba(15, 118, 110, 0.25)" strokeWidth="0.4" strokeDasharray="2,2" />
        </g>

        {/* Data pulse */}
        <g className="data-pulse">
          <circle r="1.5" fill="#0F766E" filter="url(#mediaGlow)">
            <animateMotion dur="6s" repeatCount="indefinite">
              <mpath href="#mediaPath" />
            </animateMotion>
            <animate attributeName="opacity" values="0;1;1;1;0" dur="6s" repeatCount="indefinite" />
          </circle>
          <path id="mediaPath" d="M 12 45 L 37 45 L 62 45 L 89 45 L 120 45" fill="none" stroke="none" />
        </g>

        {/* Nodes with labels */}
        <g className="nodes" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          {/* Source Input */}
          <rect x="8" y="40" width="12" height="10" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="14" y="47" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">SRC</text>
          
          {/* Transcoder */}
          <rect x="30" y="38" width="16" height="14" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="38" y="44" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">ENCODE</text>
          <text x="38" y="49" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">ffmpeg</text>
          
          {/* Storage */}
          <rect x="55" y="38" width="16" height="14" rx="1.5" fill="rgba(15, 118, 110, 0.08)" stroke="rgba(15, 118, 110, 0.5)" strokeWidth="0.7" />
          <text x="63" y="44" textAnchor="middle" fill="#0F766E" fontSize="4" fontWeight="500">STORE</text>
          <text x="63" y="49" textAnchor="middle" fill="rgba(15, 118, 110, 0.7)" fontSize="3">s3</text>
          
          {/* CDN (Core) */}
          <rect x="82" y="36" width="16" height="18" rx="2" fill="rgba(15, 118, 110, 0.15)" stroke="#0F766E" strokeWidth="1" />
          <text x="90" y="44" textAnchor="middle" fill="#0F766E" fontSize="5" fontWeight="600">CDN</text>
          <text x="90" y="50" textAnchor="middle" fill="rgba(15, 118, 110, 0.8)" fontSize="3">edge</text>
          
          {/* Output Channels */}
          <rect x="115" y="18" width="10" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="120" y="24" textAnchor="middle" fill="#0F766E" fontSize="3.5">WEB</text>
          
          <rect x="115" y="41" width="10" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.1)" stroke="rgba(15, 118, 110, 0.6)" strokeWidth="0.8" />
          <text x="120" y="47" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">APP</text>
          
          <rect x="115" y="64" width="10" height="8" rx="1.5" fill="rgba(15, 118, 110, 0.06)" stroke="rgba(15, 118, 110, 0.4)" strokeWidth="0.6" />
          <text x="120" y="70" textAnchor="middle" fill="#0F766E" fontSize="3.5">API</text>
        </g>

        {/* Technical annotations */}
        <g className="annotations" style={{ fontFamily: 'JetBrains Mono, monospace' }}>
          <text x="90" y="60" textAnchor="middle" fill="#0F766E" fontSize="3.5" fontWeight="500">p95 45ms</text>
          <text x="75" y="17" textAnchor="middle" fill="rgba(15, 118, 110, 0.6)" fontSize="3">cache_hit</text>
        </g>

        {/* Status indicator */}
        <g className="status">
          <circle cx="6" cy="6" r="2.5" fill="#10B981">
            <animate attributeName="opacity" values="1;0.5;1" dur="2s" repeatCount="indefinite" />
          </circle>
          <text x="12" y="8" fill="#10B981" fontSize="3.5" fontWeight="600" style={{ fontFamily: 'JetBrains Mono, monospace' }}>EDGE</text>
        </g>
      </svg>
    </div>
  );
}

export default IzmexMediaCircuit;
